
import Vue from 'vue';
import { mapState, mapGetters } from 'vuex';
import mixins from 'vue-typed-mixins';
import format from 'date-fns/format';

import api from '@/api';

import i18n from '@/components/i18n';
import { imageSizeMixin } from '@/components/mixins/image-size';
import ModalWithSidebar from '@/components/layout/modal-with-sidebar.vue';
import Modal from '@/components/layout/modal.vue';
import WistiaVideo from '@/components/layout/wistia-video.vue';
import SelectInput from '@/components/controls/select-input.vue';
import SubmitButton from '@/components/controls/submit-button.vue';
import Image from '@/store/models/image';
import BannerStandImage from '@/store/modules/banner-stand-images/banner-stand-image';
import Location from '@/store/modules/locations/location';

interface SelectionRow {
  id: number;
  productId: number | string;
  productTitle: string;
  bannerStandImageId: number;
  images: Image[];
  selectedImageIndex: number;
  isLoading: boolean;
}

interface OrderItem {
  productId: number;
  productTitle: string;
  bannerStandImageId: number;
  image: Image;
}

interface ProductOption {
  id: number;
  title: string;
}

let rowIdCounter = 0;

function createEmptyRow(): SelectionRow {
  return {
    id: ++rowIdCounter,
    productId: '',
    productTitle: '',
    bannerStandImageId: 0,
    images: [],
    selectedImageIndex: -1,
    isLoading: false,
  };
}

export default mixins(imageSizeMixin).extend({
  name: 'BannerStandModal',
  $_veeValidate: {
    validator: 'new',
  },
  components: {
    i18n,
    ModalWithSidebar,
    Modal,
    WistiaVideo,
    SelectInput,
    SubmitButton,
  },
  data() {
    return {
      formStep: 1,
      isSubmitting: false,
      isSuccess: false,
      isError: false,
      isHowToModalOpen: false,
      isImagePreviewOpen: false,
      previewImage: null as Image | null,
      
      // Products data
      products: [] as ProductOption[],
      orderedProductIds: [] as number[],
      isLoadingProducts: false,
      
      // Selection rows (multiple product selections)
      selectionRows: [createEmptyRow()] as SelectionRow[],
      
      // Final order items (populated when order is submitted)
      orderItems: [] as OrderItem[],
      
      // Locations data
      locations: [] as Location[],
      isLoadingLocations: false,
      selectedLocationId: '' as string | number,
      
      // Delivery info
      orderDate: '',
    };
  },
  computed: {
    ...mapState('user/profile', ['currentProfile']),
    ...mapGetters('user/profile', ['hasPractice']),
    
    practiceName(): string {
      return this.currentProfile?.practice?.title || '';
    },
    
    selectedLocation(): Location | undefined {
      if (!this.selectedLocationId) return undefined;
      return this.locations.find(loc => loc.id === Number(this.selectedLocationId));
    },
    
    deliveryAddress(): string {
      if (this.selectedLocation) {
        return this.selectedLocation.humanReadableAddress;
      }
      return '';
    },
    
    // Get all rows that have a product and image selected
    completedRows(): SelectionRow[] {
      return this.selectionRows.filter(row => 
        row.productId !== '' && 
        row.selectedImageIndex >= 0 && 
        row.images.length > 0
      );
    },
    
    // Check if we can add more product rows
    canAddMoreRows(): boolean {
      // Check if the last row has a product and image selected
      const lastRow = this.selectionRows[this.selectionRows.length - 1];
      const lastRowComplete = lastRow && 
        lastRow.productId !== '' && 
        lastRow.selectedImageIndex >= 0 && 
        lastRow.images.length > 0;
      
      if (!lastRowComplete) {
        return false;
      }
      
      // Check if there are still products available (not selected and not already ordered)
      const selectedProductIds = this.selectionRows
        .filter(row => row.productId !== '')
        .map(row => Number(row.productId));
      const hasMoreProducts = this.products.some(product => 
        !selectedProductIds.includes(product.id) && !this.orderedProductIds.includes(product.id)
      );
      
      return hasMoreProducts;
    },
    
    canProceedToStep2(): boolean {
      return this.completedRows.length > 0;
    },
    
    // Get order items for display in confirmation step
    confirmedOrderItems(): OrderItem[] {
      if (this.orderItems.length > 0) {
        return this.orderItems;
      }
      // Fallback to completedRows if orderItems not populated
      return this.completedRows.map(row => ({
        productId: Number(row.productId),
        productTitle: row.productTitle,
        bannerStandImageId: row.bannerStandImageId,
        image: row.images[row.selectedImageIndex],
      }));
    },
  },
  methods: {
    async loadProducts() {
      this.isLoadingProducts = true;
      try {
        // Get products that have banner stand images (with ordered info for disabling)
        const usedProductsResponse = await api.bannerStandImages.getUsedProducts({
          language_code: this.currentProfile?.languageCode || 'en',
          include_ordered: true
        });
        
        // Handle response format - can be array or object with products/ordered
        let usedProductIds: number[];
        if (Array.isArray(usedProductsResponse.data)) {
          usedProductIds = usedProductsResponse.data;
          this.orderedProductIds = [];
        } else {
          usedProductIds = usedProductsResponse.data.products;
          this.orderedProductIds = usedProductsResponse.data.ordered;
        }
        
        // Get practice-owned product IDs from user profile
        const practiceProducts = this.currentProfile?.practice?.products || {};
        const practiceProductIds = Object.keys(practiceProducts).map(id => Number(id));
        
        if (usedProductIds.length > 0) {
          // Load product details for available products
          const productsResponse = await api.products.list({ status: 1 });
          this.products = productsResponse.data.data
            .filter((product: any) => 
              // Must have banner stand images AND be owned by practice
              usedProductIds.includes(product.id) && practiceProductIds.includes(product.id)
            )
            .map((product: any) => ({
              id: product.id,
              title: product.title,
            }));
        }
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        this.isLoadingProducts = false;
      }
    },
    
    async loadLocations() {
      const practiceId = this.currentProfile?.practiceId;
      if (!practiceId) return;
      
      this.isLoadingLocations = true;
      try {
        const response = await api.listings.list({ practice_id: practiceId });
        this.locations = response.data.data.map((item: any) => Location.fromJSON(item));
        
        // Auto-select first location if available
        if (this.locations.length > 0 && !this.selectedLocationId) {
          this.selectedLocationId = this.locations[0].id;
        }
      } catch (error) {
        console.error('Error loading locations:', error);
      } finally {
        this.isLoadingLocations = false;
      }
    },
    
    // Check if a product is already selected in another row
    isProductSelected(productId: number, currentRowIndex: number): boolean {
      return this.selectionRows.some((row, index) => 
        index !== currentRowIndex && Number(row.productId) === productId
      );
    },
    
    // Check if a product has already been ordered by the practice
    isProductOrdered(productId: number): boolean {
      return this.orderedProductIds.includes(productId);
    },
    
    async onProductChange(rowIndex: number, productId: number | string) {
      const row = this.selectionRows[rowIndex];
      if (!row) return;
      
      row.productId = productId;
      row.selectedImageIndex = -1;
      row.images = [];
      row.bannerStandImageId = 0;
      row.productTitle = '';
      
      if (!productId) {
        return;
      }
      
      // Find product title
      const product = this.products.find(p => p.id === Number(productId));
      if (product) {
        row.productTitle = product.title;
      }
      
      row.isLoading = true;
      
      try {
        const response = await api.bannerStandImages.list({
          product_id: Number(productId),
          status: 1,
        });
        
        if (response.data.data && response.data.data.length > 0) {
          const bannerStandImageData = response.data.data[0];
          const bannerStandImage = new BannerStandImage();
          bannerStandImage.fromJSON(bannerStandImageData);
          
          row.bannerStandImageId = bannerStandImage.id;
          row.images = bannerStandImage.images.filter((img): img is Image => img !== undefined);
        }
      } catch (error) {
        console.error('Error loading banner stand images:', error);
      } finally {
        row.isLoading = false;
      }
    },
    
    selectImage(rowIndex: number, imageIndex: number) {
      const row = this.selectionRows[rowIndex];
      if (row) {
        row.selectedImageIndex = imageIndex;
      }
    },
    
    addSelectionRow() {
      if (!this.canAddMoreRows) return;
      this.selectionRows.push(createEmptyRow());
    },
    
    removeSelectionRow(rowIndex: number) {
      if (this.selectionRows.length > 1) {
        this.selectionRows.splice(rowIndex, 1);
      }
    },
    
    openImagePreview(image: Image) {
      console.log('previewImage:', image);
      this.previewImage = image;
      this.isImagePreviewOpen = true;
    },
    
    closeImagePreview() {
      this.isImagePreviewOpen = false;
      this.previewImage = null;
    },
    
    goToStep1() {
      this.formStep = 1;
    },
    
    goToStep2() {
      if (!this.canProceedToStep2) return;
      this.formStep = 2;
    },
    
    async submitOrder() {
      this.isSubmitting = true;
      this.isError = false;
      
      try {
        // Convert completed rows to order items for display
        this.orderItems = this.completedRows.map(row => ({
          productId: Number(row.productId),
          productTitle: row.productTitle,
          bannerStandImageId: row.bannerStandImageId,
          image: row.images[row.selectedImageIndex],
        }));
        
        // Prepare API request data
        const orderData = {
          location_id: Number(this.selectedLocationId),
          items: this.completedRows.map(row => ({
            product_id: Number(row.productId),
            banner_stand_image_id: row.bannerStandImageId,
          })),
        };
        
        // Submit order to API
        await api.bannerStandImages.submitOrder(orderData);
        
        this.orderDate = format(new Date(), 'd MMMM yyyy');
        this.isSuccess = true;
      } catch (error) {
        console.error('Error submitting order:', error);
        this.isError = true;
      } finally {
        this.isSubmitting = false;
      }
    },
    
    onClose() {
      if (!this.isSubmitting) {
        this.resetForm();
        this.$emit('close');
      }
    },
    
    resetForm() {
      this.formStep = 1;
      this.isSuccess = false;
      this.isError = false;
      this.selectionRows = [createEmptyRow()];
      this.orderItems = [];
      this.selectedLocationId = '';
    },
    
    openHowToModal() {
      this.isHowToModalOpen = true;
    },
  },
  mounted() {
    this.loadProducts();
    this.loadLocations();
  },
});
